import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetProjects } from "../../hooks/projects/useGetProjects";
import { useDeleteProject } from "../../hooks/projects/useDeleteProject";
import { useMassDestroyProjects } from "../../hooks/projects/useMassDestroyProjects";
import { useMassRestoreProjects } from "../../hooks/projects/useMassRestoreProjects";
import { useMassForceDeleteProjects } from "../../hooks/projects/useMassForceDeleteProjects";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ProjectsTableRow from "./Table/ProjectsTableRow";
import { ProjectsTableColumns } from "./Table/ProjectsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const ProjectsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("projects.view"),
            create: has("projects.create"),
            edit: has("projects.update"),
            delete: has("projects.delete"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");
    const [showInHomeFilter, setShowInHomeFilter] = useState("");

    // CRUD Hooks
    const { mutate: deleteProjectMutate, isPending: isDelete } = useDeleteProject();
    const { mutate: massDestroyProjectsMutate, isPending: isDeleteAll } = useMassDestroyProjects();
    const { mutate: massRestoreProjectsMutate, isPending: isRestoreAll } = useMassRestoreProjects();
    const { mutate: massForceDeleteProjectsMutate, isPending: isForceDeleteAll } = useMassForceDeleteProjects();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/projects",
        deleteSingle: deleteProjectMutate,
        deleteAll: massDestroyProjectsMutate,
        restoreSingle: (id, options) => massRestoreProjectsMutate({ items: [id] }, options),
        restoreAll: massRestoreProjectsMutate,
        forceDeleteSingle: (id, options) => massForceDeleteProjectsMutate({ items: [id] }, options),
        forceDeleteAll: massForceDeleteProjectsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            show_in_home: showInHomeFilter !== "" ? showInHomeFilter : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, showInHomeFilter, order]
    );

    const { isLoading, error, data } = useGetProjects(params);
    const projects = data?.data || [];
    const meta = data?.meta || {};

    const filters = useMemo(() => [
        {
            key: "show_in_home",
            label: { ar: "عرض في الرئيسية", en: "Show in Home" },
            value: showInHomeFilter,
            onChange: setShowInHomeFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نعم", en: "Yes" } },
                { value: "0", label: { ar: "لا", en: "No" } },
            ]
        }
    ], [showInHomeFilter]);

    const columns = useMemo(() => ProjectsTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "المشاريع", en: "Projects" }}
            columns={columns}
            items={projects}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, projects)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/projects/create")}
            addButtonLabel={{ ar: "إضافة مشروع", en: "Add Project" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <ProjectsTableRow
                    {...params}
                    key={params.item.id}
                    project={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default ProjectsTable;
